import { supabase } from "@/integrations/supabase/client";

export interface RouteResult {
  distanceKm: number;
  durationMin: number;
  summary?: string;
  overviewPolyline?: string;
  legs?: { start: string; end: string; distanceKm: number; durationMin: number }[];
}

/**
 * Calls Google Directions API via edge function for accurate route data.
 * Falls back to Haversine if unavailable.
 */
export async function getRouteDistance(
  waypoints: [number, number][]
): Promise<RouteResult[] | null> {
  if (waypoints.length < 2) return null;

  // Primary: Google Directions via edge function
  try {
    const origin = `${waypoints[0][0]},${waypoints[0][1]}`;
    const destination = `${waypoints[waypoints.length - 1][0]},${waypoints[waypoints.length - 1][1]}`;
    const intermediate = waypoints.slice(1, -1).map(([lat, lng]) => `${lat},${lng}`);

    const { data, error } = await supabase.functions.invoke("google-directions", {
      body: {
        origin,
        destination,
        waypoints: intermediate.length > 0 ? intermediate : undefined,
        alternatives: waypoints.length === 2,
      },
    });

    if (!error && data?.routes?.length > 0) {
      return data.routes as RouteResult[];
    }
  } catch {
    // Fall through to haversine
  }

  // Fallback: Haversine
  let total = 0;
  for (let i = 1; i < waypoints.length; i++) {
    total += haversineKm(
      waypoints[i - 1][0], waypoints[i - 1][1],
      waypoints[i][0], waypoints[i][1]
    );
  }
  return [{ distanceKm: total, durationMin: 0 }];
}

export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export interface Currency {
  code: string;
  symbol: string;
  label: string;
}

export const CURRENCIES: Currency[] = [
  { code: "SEK", symbol: "kr", label: "Svenska kronor (SEK)" },
  { code: "EUR", symbol: "€", label: "Euro (EUR)" },
  { code: "NOK", symbol: "kr", label: "Norska kronor (NOK)" },
  { code: "DKK", symbol: "kr", label: "Danska kronor (DKK)" },
  { code: "GBP", symbol: "£", label: "Brittiska pund (GBP)" },
  { code: "CHF", symbol: "CHF", label: "Schweiziska franc (CHF)" },
  { code: "PLN", symbol: "zł", label: "Polska zloty (PLN)" },
  { code: "CZK", symbol: "Kč", label: "Tjeckiska kronor (CZK)" },
  { code: "HUF", symbol: "Ft", label: "Ungerska forint (HUF)" },
  { code: "HRK", symbol: "kn", label: "Kroatiska kuna (HRK)" },
  { code: "USD", symbol: "$", label: "US Dollar (USD)" },
  { code: "TRY", symbol: "₺", label: "Turkiska lira (TRY)" },
];

const RATES_FROM_SEK: Record<string, number> = {
  SEK: 1,
  EUR: 0.087,
  NOK: 1.01,
  DKK: 0.65,
  GBP: 0.074,
  CHF: 0.082,
  PLN: 0.37,
  CZK: 2.15,
  HUF: 34.5,
  HRK: 0.65,
  USD: 0.093,
  TRY: 3.0,
};

/**
 * Optimizes stop order by projecting each stop onto the start→destination vector
 * and sorting by projection distance. This creates a natural geographic flow
 * instead of zig-zag routes that nearest-neighbor produces.
 *
 * @param startLat  Starting point latitude
 * @param startLng  Starting point longitude
 * @param destLat   Destination latitude
 * @param destLng   Destination longitude
 * @param stops     Array of stops with latitude/longitude
 * @returns         The same stops sorted in natural geographic order with updated indices
 */
export function optimizeStopOrder<T extends { latitude: number; longitude: number }>(
  startLat: number,
  startLng: number,
  destLat: number,
  destLng: number,
  stops: T[],
): T[] {
  if (stops.length <= 1) return [...stops];

  // Vector from start to destination
  const dx = destLng - startLng;
  const dy = destLat - startLat;
  const lenSq = dx * dx + dy * dy;

  if (lenSq < 1e-12) {
    // Start and destination are (nearly) the same point — fall back to distance from start
    return [...stops].sort((a, b) => {
      const da = haversineKm(startLat, startLng, a.latitude, a.longitude);
      const db = haversineKm(startLat, startLng, b.latitude, b.longitude);
      return da - db;
    });
  }

  // Project each stop onto the start→dest line and compute perpendicular distance
  const scored = stops.map((stop) => {
    const px = stop.longitude - startLng;
    const py = stop.latitude - startLat;
    // Scalar projection (0 = at start, 1 = at dest)
    const t = (px * dx + py * dy) / lenSq;
    // Perpendicular distance (used as tiebreaker)
    const perpX = px - t * dx;
    const perpY = py - t * dy;
    const perpDist = Math.sqrt(perpX * perpX + perpY * perpY);
    return { stop, t, perpDist };
  });

  // Sort primarily by projection along route, secondarily by closeness to route line
  scored.sort((a, b) => {
    const diff = a.t - b.t;
    if (Math.abs(diff) < 0.01) return a.perpDist - b.perpDist;
    return diff;
  });

  return scored.map((s) => s.stop);
}

export function convertCurrency(amount: number, fromCode: string, toCode: string): number {
  if (fromCode === toCode) return amount;
  const fromRate = RATES_FROM_SEK[fromCode] ?? 1;
  const toRate = RATES_FROM_SEK[toCode] ?? 1;
  return (amount / fromRate) * toRate;
}
